import { useEffect, useState } from "react";
import { Search, Check, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtOdds, SPORT_META } from "@/lib/odds";
import { SportTabs } from "@/components/SportTabs";
import { ParlaySlip } from "@/components/ParlaySlip";
import { useSlip } from "@/context/SlipContext";
import { MoveChip } from "@/components/MoveChip";
import { PlayerLink } from "@/components/PlayerLink";
import { OffSeasonNotice } from "@/components/OffSeasonNotice";
import { useDataSource } from "@/lib/dataSource";

export function FormRow({ f }) {
  const hit = f.over_line_last5 || f.scored_last5;
  const [num, den] = hit ? hit.split("/").map(Number) : [0, 0];
  const tone = den === 0 ? "text-slate-300" : num / den >= 0.6 ? "text-emerald-300" : num / den <= 0.4 ? "text-red-300" : "text-amber-300";
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-[#1B2436] bg-[#0B0E14]/60 px-3 py-1.5 text-xs" data-testid="prop-form" title={`Last ${f.last.length} games (most recent first)${f.opps?.length ? ": vs " + f.opps.join(", ") : ""} — free league game log`}>
      <span className="label-caps">Game log</span>
      <span className="font-mono-num text-slate-200">{f.last.join(" · ")}</span>
      <span className="text-slate-500">L5 avg <b className="font-mono-num text-white">{f.avg5}</b></span>
      <span className="text-slate-500">Season <b className="font-mono-num text-white">{f.avg_season}</b></span>
      {hit && <span className={`font-mono-num font-bold ${tone}`} data-testid="prop-form-hit">{hit} {f.scored_last5 ? "scored" : `over ${f.line}`}</span>}
    </div>
  );
}

export function RecentStats({ r, stat }) {
  const hasLine = r.line != null && r.games.some((x) => x.actual != null);
  const max = hasLine ? Math.max(...r.games.map((x) => x.actual ?? 0), r.line) * 1.15 : 1;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-3 rounded-lg bg-[#0B0E14] px-3 py-2" data-testid="prop-recent">
      <span className="label-caps">Last {r.n}</span>
      {hasLine ? (
        <div className="relative flex h-14 items-end gap-1.5 pt-3" data-testid="prop-recent-bars" title={r.games.map((x) => `${x.date.slice(5)} vs ${x.opponent}: ${x.actual} (line ${x.line})`).join(" · ")}>
          <span className="pointer-events-none absolute inset-x-0 z-10 border-t border-dashed border-amber-400/80" style={{ bottom: `${(r.line / max) * 32}px` }} />
          {r.games.map((x) => (
            <span key={x.date} data-testid="prop-recent-bar" className="flex w-7 flex-col items-center justify-end">
              <span className={`font-mono-num mb-0.5 text-[9px] leading-none ${x.actual > r.line ? "text-emerald-300" : "text-red-300"}`}>{x.actual}</span>
              <span className={`w-full rounded-t ${x.actual > r.line ? "bg-emerald-500/80" : "bg-red-500/70"}`} style={{ height: `${Math.max((x.actual / max) * 32, 2)}px` }} />
            </span>
          ))}
        </div>
      ) : (
        <span className="flex items-center gap-1" data-testid="prop-recent-dots">{r.games.map((x) => <span key={x.date} title={`${x.date.slice(5)} vs ${x.opponent}: ${x.result}`} className={`h-2.5 w-2.5 rounded-full ${x.result === "won" ? "bg-emerald-400" : "bg-red-400"}`} />)}</span>
      )}
      <span className="text-[11px] text-slate-400" data-testid="prop-recent-summary">
        {hasLine ? <>avg <span className="font-mono-num font-bold text-white">{r.avg}</span> · <span className={`font-mono-num font-bold ${r.over_line >= Math.ceil(r.n / 2) + (r.n % 2 === 0 ? 1 : 0) ? "text-emerald-300" : "text-red-300"}`}>{r.over_line}/{r.n}</span> over today's {r.line}</> : <><span className="font-mono-num font-bold text-white">{r.hits}/{r.n}</span> hit {stat.toLowerCase()}</>}
      </span>
    </div>
  );
}

export function Ladder({ g, showHeader = true }) {
  const { legs, toggle } = useSlip();
  const [busy, setBusy] = useState("");
  const best = g.rungs.reduce((a, r) => (r.odds > a.odds ? r : a), g.rungs[0]);
  const inSlip = (r) => legs.some((l) => l.event_id === g.event_id && l.selection === r.label);
  const add = async (r) => {
    setBusy(r.prop_id);
    try {
      const { data } = await api.post(`/props/${r.prop_id}/pick`);
      toggle(data);
      toast.success(inSlip(r) ? "Removed from slip" : `${r.label} · ${fmtOdds(r.odds)} added`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  const stat = g.stat.replace(/^Over [\d.]+ /, "");
  return (
    <div data-testid="prop-group" className="glass-card fade-up p-4">
      {showHeader && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="h-4 w-1 rounded-full" style={{ background: SPORT_META[g.sport]?.color }} />
          <span className="label-caps" style={{ color: SPORT_META[g.sport]?.color }}>{SPORT_META[g.sport]?.short}</span>
          <span className="text-xs text-slate-500">{g.matchup} · {new Date(g.start_time).toDateString() === new Date().toDateString() ? new Date(g.start_time).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : new Date(g.start_time).toLocaleString([], { weekday: "short", hour: "numeric", minute: "2-digit" })}</span>
          {g.rungs[0]?.movement && <MoveChip movement={g.rungs[0].movement} compact spark={false} />}
        </div>
      )}
      <p className="mt-1.5 text-base font-semibold text-white" data-testid="prop-group-title">
        {g.player ? <><PlayerLink player={g.player} label={g.player.name} /> <span className="text-xs text-slate-500">({g.player.team})</span> · <span className="text-slate-300">{stat}</span></> : g.stat}
      </p>
      {g.recent && g.recent.n > 0 && <RecentStats r={g.recent} stat={stat} />}
      {g.form && <FormRow f={g.form} />}
      <div className="mt-3 flex flex-wrap gap-2" data-testid="prop-rungs">
        {g.rungs.map((r) => {
          const on = inSlip(r);
          const mv = r.movement;
          return (
            <button key={r.prop_id} data-testid="prop-rung" disabled={busy === r.prop_id} onClick={() => add(r)}
              className={`group relative flex min-w-[92px] flex-col items-center rounded-lg border px-3 py-2 transition-[border-color,background-color,transform] duration-200 hover:-translate-y-0.5 ${on ? "border-emerald-500 bg-emerald-500/15" : r.alt ? "border-fuchsia-500/30 bg-[#0B0E14] hover:border-fuchsia-400" : "border-[#26334D] bg-[#0B0E14] hover:border-slate-400"}`}>
              <span className="font-mono-num text-sm font-bold text-white">{r.line != null ? `${g.stat.startsWith("Under") ? "U" : "O"} ${r.line}` : r.label.replace(/^.*?\(\w+\) /, "")}</span>
              <span className={`font-mono-num text-sm font-bold ${r.odds > 0 ? "text-amber-400" : "text-slate-300"}`}>{fmtOdds(r.odds)}</span>
              <span className="text-[10px] text-slate-500">{Math.round(r.implied * 100)}% imp{r.alt ? " · ALT" : ""}</span>
              {mv && mv.direction !== "flat" && <span data-testid="prop-rung-move" className={`font-mono-num text-[10px] font-bold ${mv.direction === "in" ? "text-red-300" : "text-emerald-300"}`}>now {fmtOdds(mv.current)} {mv.direction === "in" ? "▼" : "▲"}</span>}
              {r === best && g.rungs.length > 1 && <span className="absolute -top-2 right-1 rounded-full bg-amber-400 px-1.5 text-[9px] font-black uppercase text-[#0B0E14]">Best price</span>}
              <span className="absolute -left-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-[#0B0E14] opacity-0 transition-opacity group-hover:opacity-100">{on ? <Check size={10} /> : <Plus size={10} />}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Props() {
  const [q, setQ] = useState("");
  const [sport, setSport] = useState("all");
  const [trend, setTrend] = useState("");
  const [game, setGame] = useState("");
  const [d, setD] = useState(null);
  const games = d ? [...new Map(d.groups.map((g) => [g.event_id, g])).values()].sort((a, b) => a.start_time.localeCompare(b.start_time)) : [];
  const shown = d ? d.groups.filter((g) => !game || g.event_id === game) : [];
  const ds = useDataSource();
  useEffect(() => { setGame(""); }, [sport]);
  useEffect(() => {
    const fetch = () => api.get(`/props?q=${encodeURIComponent(q)}&sport=${sport}${trend ? `&trend=${trend}` : ""}`).then((r) => setD(r.data)).catch(() => {});
    const t = setTimeout(fetch, 250);
    const poll = setInterval(fetch, 60000);
    return () => { clearTimeout(t); clearInterval(poll); };
  }, [q, sport, trend]);

  return (
    <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="props-page">
      <p className="label-caps text-emerald-400">Line shopping</p>
      <h1 className="font-display mt-2 text-4xl font-black uppercase tracking-tight text-white sm:text-5xl">Player Props Board</h1>
      <p className="mt-2 max-w-xl text-sm text-slate-400">Every prop on today's slate with all alternate lines side by side. Search a player, team or stat, compare prices, and add any single line to your slip.</p>
      <div className="mt-6 grid gap-8 lg:grid-cols-[1fr_360px]">
        <section className="min-w-0">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input data-testid="prop-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search player, team or stat — e.g. Lakers points, Alcaraz aces, passing yds" className="input-dark !pl-9" />
          </div>
          <div className="mt-4"><SportTabs value={sport} onChange={setSport} /></div>
          <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="prop-trend-filters">
            <span className="label-caps mr-1">Trend</span>
            <button data-testid="prop-trend-all" onClick={() => setTrend("")} className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${!trend ? "border-emerald-500 bg-emerald-500/15 text-emerald-300" : "border-[#26334D] text-slate-400 hover:text-white"}`}>All players</button>
            <button data-testid="prop-trend-hot" onClick={() => setTrend(trend === "hot" ? "" : "hot")} className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${trend === "hot" ? "border-orange-500 bg-orange-500/15 text-orange-300" : "border-[#26334D] text-slate-400 hover:text-white"}`}>Cleared today's line 4+ of last 5</button>
            <button data-testid="prop-trend-cold" onClick={() => setTrend(trend === "cold" ? "" : "cold")} className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${trend === "cold" ? "border-sky-500 bg-sky-500/15 text-sky-300" : "border-[#26334D] text-slate-400 hover:text-white"}`}>Under the line 4+ of last 5</button>
          </div>
          <p className="mt-3 text-xs text-slate-500" data-testid="prop-count">{d ? `${d.count} prop ladders across ${d.games} games${d.ahead_days ? " · NFL & college football open the moment books post them, up to a week ahead" : ""}${d.trend ? ` · ${d.trend === "hot" ? "players clearing today's number" : "players falling short of today's number"}` : ""}` : "Loading…"}</p>
          {games.length > 1 && (
            <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="prop-game-filter-row">
              <span className="label-caps mr-1">Game</span>
              <select data-testid="prop-game-filter" value={game} onChange={(e) => setGame(e.target.value)} className="rounded-lg border border-[#26334D] bg-[#0B0F17] px-3 py-1.5 text-xs font-semibold text-white outline-none focus:border-emerald-500">
                <option value="">All {games.length} games</option>
                {games.map((g) => <option key={g.event_id} value={g.event_id}>{new Date(g.start_time).toLocaleString([], { weekday: "short", hour: "numeric", minute: "2-digit" })} · {g.matchup}</option>)}
              </select>
              {game && <span className="text-xs text-slate-500" data-testid="prop-game-count">{shown.length} ladders</span>}
            </div>
          )}
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {shown.map((g) => <Ladder key={g.key} g={g} />)}
            {d && d.groups.length === 0 && (
              trend || q || ["all", "mine"].includes(sport)
                ? <p className="text-sm text-slate-500" data-testid="prop-empty">{trend ? "No players fit this trend in the current view — try All Sports or clear the search." : 'No props match. Try a team name, a player, or a stat like "rebounds".'}</p>
                : ds.props_sports?.length && !ds.props_sports.includes(sport)
                  ? <p className="text-sm text-slate-500" data-testid="prop-empty">Live player props are pulled for {ds.props_sports.map((k) => SPORT_META[k]?.short || k).join(", ")} games — pick one of those leagues to shop lines.</p>
                  : <div className="md:col-span-2"><OffSeasonNotice sport={sport} context="props" onBrowse={() => setSport("all")} fallback={<p className="text-sm text-slate-500" data-testid="prop-empty">No props posted for this league yet — books usually post player props the morning of the game.</p>} /></div>
            )}
          </div>
        </section>
        <div className="hidden lg:sticky lg:top-24 lg:block lg:self-start"><ParlaySlip /></div>
      </div>
    </main>
  );
}
